import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useRouter } from 'expo-router'
import { Spinner } from '@/src/components/ui/Spinner'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { appQueryOptions } from '@/src/queries/view-queries'
import { useSuspenseQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { VocaliseLogo } from '../../../components/VocaliseLogo'
import {
  buildOnboardingStartedProperties,
  buildOnboardingStepCompletedProperties,
  MixpanelEvents,
  trackEvent,
} from '../../../services/analytics'
import { colors } from '../../../theme'
import {
  INTRO_BODY_STYLE,
  INTRO_HEADLINE_STYLE,
  INTRO_LABEL_STYLE,
} from '../constants/onboardingStyles'
import { useOnboardingSignInMutation } from '../hooks/useOnboardingMutations'

const introAdditionalTextSectionsSchema = z.object({
  signInText: z.string(),
})

export interface OnboardingIntroContent {
  headline?: string
  subheadline?: string
  body?: string
  primaryButtonText?: string
  secondaryPrefixText: string
  secondaryButtonText?: string
}

interface OnboardingIntroStepProps {
  returnTo?: string | null
}

export function OnboardingIntroStep({ returnTo }: OnboardingIntroStepProps) {
  const router = useRouter()

  const [isTransitioningToProfile, setIsTransitioningToProfile] =
    useState(false)

  const signInMutation = useOnboardingSignInMutation(returnTo)
  const locale = useContentfulLocale()

  const { data: app } = useSuspenseQuery(appQueryOptions(locale))
  const introView = app.onboardingIntroStep
  const introAdditionalTextSections = useMemo(() => {
    return introAdditionalTextSectionsSchema.parse(
      introView?.additionalTextSections,
    )
  }, [introView?.additionalTextSections])

  const introContent: OnboardingIntroContent = useMemo(
    () => ({
      headline: introView?.headline ?? undefined,
      subheadline: introView?.subheadline ?? undefined,
      body: introView?.textContent ?? undefined,
      primaryButtonText: introView?.primaryButtonText ?? undefined,
      secondaryPrefixText: introAdditionalTextSections.signInText,
      secondaryButtonText: introView?.secondaryButtonText ?? undefined,
    }),
    [introAdditionalTextSections.signInText, introView],
  )

  useEffect(() => {
    trackEvent(
      MixpanelEvents.OnboardingStarted,
      buildOnboardingStartedProperties({ totalSteps: 2 }),
    )
  }, [])

  const handleSignIn = useCallback(() => {
    void signInMutation.mutateAsync()
  }, [signInMutation])

  const completeIntroToProfileTransition = useCallback(() => {
    trackEvent(
      MixpanelEvents.OnboardingStepCompleted,
      buildOnboardingStepCompletedProperties({
        stepNumber: 1,
        stepName: 'intro',
      }),
    )
    router.setParams({ step: 'profile' })
    setIsTransitioningToProfile(false)
  }, [router])

  const handleStartTrialFromIntro = useCallback(() => {
    if (isTransitioningToProfile) return

    setIsTransitioningToProfile(true)
    completeIntroToProfileTransition()
  }, [completeIntroToProfileTransition, isTransitioningToProfile])

  return (
    <>
      <Text
        className="mb-4"
        style={[{ marginBottom: 16 }, INTRO_HEADLINE_STYLE]}
      >
        {introContent.headline}
        {'\n'}
        {introContent.subheadline}
      </Text>
      <Text className="mb-6" style={[{ marginBottom: 24 }, INTRO_BODY_STYLE]}>
        {introContent.body}
      </Text>

      <Pressable
        className="h-12 rounded-lg bg-primary items-center justify-center mb-4"
        onPress={handleStartTrialFromIntro}
        disabled={isTransitioningToProfile}
        testID="e2e-onboarding-start-trial-intro"
      >
        <Text style={[INTRO_LABEL_STYLE, { color: colors.primaryForeground }]}>
          {introContent.primaryButtonText}
        </Text>
      </Pressable>

      <View className="flex-row justify-center items-center mb-8">
        <Text className="mr-2" style={INTRO_LABEL_STYLE}>
          {introContent.secondaryPrefixText}
        </Text>
        <Pressable
          onPress={handleSignIn}
          disabled={signInMutation.isPending}
          testID="e2e-onboarding-sign-in"
        >
          {signInMutation.isPending ? (
            <Spinner color={colors.primary} />
          ) : (
            <Text style={[INTRO_LABEL_STYLE, { color: colors.primary }]}>
              {introContent.secondaryButtonText}
            </Text>
          )}
        </Pressable>
      </View>
      <VocaliseLogo width={140} />
    </>
  )
}
